import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { StockTake, StockTakeItem } from "@/data/mockStockTakes";
import { mockWarehouses } from "@/data/mockWarehousesLocations";

interface CreateStockTakeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (stockTake: StockTake) => void;
}

export function CreateStockTakeDialog({
  open,
  onOpenChange,
  onSave,
}: CreateStockTakeDialogProps) {
  const [formData, setFormData] = useState({
    description: "",
    warehouseCode: "",
    notes: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      setFormData({
        description: "",
        warehouseCode: "",
        notes: "",
      });
      setErrors({});
    }
  }, [open]);

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.description.trim()) {
      newErrors.description = "Description is required";
    }

    if (!formData.warehouseCode) {
      newErrors.warehouseCode = "Warehouse is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const generateReference = () => {
    const date = new Date();
    const year = date.getFullYear();
    const random = Math.floor(Math.random() * 1000)
      .toString()
      .padStart(3, "0");
    return `ST-${year}-${random}`;
  };

  const generateMockItems = (count: number = 30): StockTakeItem[] => {
    const items: StockTakeItem[] = [];
    const locations = ["A01-R01-S01-B01", "A01-R01-S01-B02", "A01-R01-S02-B01", "B01-R01-S01-B01"];

    for (let i = 0; i < count; i++) {
      const index = i + 1;
      items.push({
        id: `item-${Date.now()}-${index}`,
        stockCode: `STK${String(index).padStart(3, "0")}`,
        stockDescription: `Stock Item ${index}`,
        location: locations[Math.floor(Math.random() * locations.length)],
        systemQuantity: Math.floor(Math.random() * 200) + 10,
        countedQuantity: null,
        variance: null,
      });
    }

    return items;
  };

  const handleSave = () => {
    if (!validate()) return;

    const warehouse = mockWarehouses.find((w) => w.code === formData.warehouseCode);
    if (!warehouse) return;

    const items = generateMockItems();

    const newStockTake: StockTake = {
      id: `ST-${Date.now()}`,
      reference: generateReference(),
      description: formData.description,
      warehouse: warehouse.name,
      warehouseCode: formData.warehouseCode,
      status: "Started",
      createdBy: "Current User",
      createdAt: new Date(),
      items,
      totalItems: items.length,
      countedItems: 0,
      varianceCount: 0,
      notes: formData.notes || undefined,
      lastSavedAt: new Date(),
      lastSavedBy: "Current User",
    };

    onSave(newStockTake);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Create Stock Take</DialogTitle>
          <DialogDescription>
            Create a new stock take to count physical inventory
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">
              Description <span className="text-destructive">*</span>
            </Label>
            <Input
              id="description"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              placeholder="e.g., Monthly Stock Take - January 2024"
              className={errors.description ? "border-destructive" : ""}
            />
            {errors.description && (
              <p className="text-sm text-destructive">{errors.description}</p>
            )}
          </div>

          {/* Warehouse */}
          <div className="space-y-2">
            <Label htmlFor="warehouse">
              Warehouse <span className="text-destructive">*</span>
            </Label>
            <Select
              value={formData.warehouseCode}
              onValueChange={(value) =>
                setFormData({ ...formData, warehouseCode: value })
              }
            >
              <SelectTrigger
                className={errors.warehouseCode ? "border-destructive" : ""}
              >
                <SelectValue placeholder="Select warehouse" />
              </SelectTrigger>
              <SelectContent>
                {mockWarehouses
                  .filter((w) => w.active)
                  .map((warehouse) => (
                    <SelectItem key={warehouse.code} value={warehouse.code}>
                      {warehouse.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            {errors.warehouseCode && (
              <p className="text-sm text-destructive">{errors.warehouseCode}</p>
            )}
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes (Optional)</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) =>
                setFormData({ ...formData, notes: e.target.value })
              }
              placeholder="Add any notes or instructions for this stock take"
              rows={3}
            />
          </div>

          {/* Info */}
          <div className="rounded-lg bg-muted p-3 text-sm">
            <p className="text-muted-foreground">
              A new stock take will be created with status "Started". You can begin
              counting items immediately after creation.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Create Stock Take</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
